// عمليات الغرف والدردشة — Realtime Database (مجاني بلا فوترة)
import {
  ref,
  get,
  set,
  push,
  remove,
  query,
  orderByChild,
  equalTo,
  limitToLast,
  onValue,
  update,
  onDisconnect,
} from "firebase/database";
import { rtdb } from "@/lib/firebase/config";

export type RoomType = "public" | "private" | "teacher";

export interface Room {
  id: string;
  name: string;
  /** الشُّعب المستهدَفة: `{all:true}` أو خريطة معرّفات — تُستعمل في الاكتشاف */
  branches?: Record<string, boolean> | null;
  type: RoomType;
  ownerId: string;
  ownerName: string;
  ownerRole?: "teacher";
  subject: string | null;
  createdAt: number | null;
  isPaid?: boolean;
  price?: number;
}

export interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  text?: string;
  type: "text" | "image" | "file";
  attachmentId?: string;
  fileName?: string;
  createdAt: number | null;
}

// مُعرّف غرفة عشوائي وآمن (منفصل عن الاسم — يحلّ مشكلة التصادم في الكود القديم)
export function generateRoomId(): string {
  return Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 6);
}

/* ════════════════════════════════════════════════════════════
   `roomsPublic` — لماذا وُجدت

   🐛 ثغرة قديمة، وأوسع ممّا بدت:

       "rooms": { ".read": "auth != null",
                  "$roomId": { ".read": "auth != null" } }

   والقراءة في RTDB **تتوارث إلى كل الأبناء**. فمنح القراءة عند
   `rooms/$roomId` يمنحها ضمناً عند `messages` و`files` و`attachments`.
   أي أنّ أيّ حساب مسجَّل — بطلبٍ واحد ولا يحتاج أكثر من متصفّح —
   يقرأ دردشة أيّ غرفة مدفوعة وملفّاتها كاملةً بلا أن يدفع.

   والأسوأ أنّ التطبيق نفسه كان يفعلها في كل مرّة: `listPublicRooms`
   و`findRoomByName` كانتا تقرآن **العقدة `rooms` كلّها** لعرض قائمة
   أسماء — أي تنزيل كل دردشات المنصّة على هاتف كل زائر لصفحة الغرف.
   ثغرة أمنية وفاتورة نطاق في آن.

   ولا يُصلَح هذا بتضييق القاعدة وحدها: القائمة تحتاج فعلاً إلى
   بيانات وصفية عامّة. فيلزم فصل ما هو عامّ عمّا هو خاصّ:

     roomsPublic/$roomId   الاسم، النوع، المادة، المالك، السعر…
                           يقرأها كل مسجَّل — لا محتوى فيها إطلاقاً.

     rooms/$roomId         المحتوى: الدردشة، الملفّات، المرفقات.
                           لا يقرأها إلّا من يستحقّ (مجانية، أو مالك،
                           أو مشرف، أو دافع).

   لماذا الغرف الخاصّة أيضاً في `roomsPublic`؟
   لأنّ «الانضمام بكتابة الاسم» يبحث بالاسم قبل أن يكون الباحث
   عضواً — فبلا فهرس عامّ للأسماء تسقط الميزة. والمكشوف اسمٌ ونوع
   ومالك فقط، وهي بيانات يعرفها كل من دُعي إلى الغرفة أصلاً. ولا
   يُعرض في القوائم: `listPublicRooms` تستبعد `private` كما كانت.
   ════════════════════════════════════════════════════════════ */

/** البيانات الوصفية العامّة — ما يجوز أن يراه كل مسجَّل، ولا شيء غيره */
function publicMirror(room: Omit<Room, "id">): Record<string, unknown> {
  return {
    name: room.name,
    type: room.type,
    subject: room.subject ?? null,
    ownerId: room.ownerId,
    ownerName: room.ownerName,
    ownerRole: room.ownerRole ?? null,
    branches: room.branches ?? { all: true },
    createdAt: room.createdAt ?? null,
    isPaid: room.isPaid ?? null,
    price: room.price ?? null,
  };
}

export async function createRoom(input: {
  name: string;
  type: RoomType;
  subject?: string;
  ownerId: string;
  ownerName: string;
  ownerRole?: "teacher";
  isPaid?: boolean;
  price?: number;
  branches?: Record<string, boolean> | null;
}): Promise<string> {
  const id = generateRoomId();
  const data: Record<string, unknown> = {
    name: input.name.trim(),
    type: input.type,
    subject: input.subject ?? null,
    ownerId: input.ownerId,
    ownerName: input.ownerName,
    ownerRole: input.ownerRole ?? null,
    branches: input.branches ?? { all: true },
    createdAt: Date.now(),
  };
  if (input.isPaid && input.price && input.price > 0) {
    data.isPaid = true;
    data.price = input.price;
  }

  /* ⚠️ ترتيب مقصود: الغرفة أوّلاً ثمّ المرآة، لا الاثنتان في تحديث
     واحد.

     قاعدة الكتابة في `roomsPublic/$roomId` تتحقّق من الملكيّة في
     `rooms/$roomId` — لا ممّا يدّعيه الكاتب في المرآة نفسها. وبلا
     هذا التحقّق يستطيع أيّ حساب أن يُنشئ مدخلاً وهمياً باسم غرفة
     موجودة فيسرق إليه من يبحث عنها بالاسم.

     والقاعدة تقرأ الحالة **السابقة** للكتابة، فلو كُتبت العقدتان في
     تحديث واحد لما وجدت الغرفة بعد ولرُفضت المرآة دائماً.

     وإن انقطع الاتصال بين الكتابتين نشأت غرفة بلا مرآة — أي لا
     تظهر في القوائم ولا تُوجد بالاسم. وهذا مؤقّت: `syncRoomPublic`
     تُصلحه عند أوّل فتح من المالك، وهو على وشك فتحها لأنّه أنشأها
     للتوّ. غرفةٌ متأخّرة عن القائمة دقيقةً أهون من مدخل وهمي لا
     يملكه أحد. */
  await set(ref(rtdb, `rooms/${id}`), data);
  await set(ref(rtdb, `roomsPublic/${id}`), publicMirror(data as unknown as Omit<Room, "id">));
  return id;
}

/**
 * مزامنة المرآة العامّة — تُستدعى عند تعديل بيانات الغرفة الوصفية.
 *
 * وهي أيضاً **آليّة الترحيل**: الغرف التي أُنشئت قبل وجود
 * `roomsPublic` لا مرآة لها. فبدل سكربت يُشغَّل مرّةً على الإنتاج
 * بمفتاح خدمة (وقد يُنسى أو يُشغَّل ناقصاً)، يُصلح المالك غرفته من
 * نفسه عند أوّل مرّة يفتحها — وهو الوحيد الذي تسمح له القاعدة
 * بالكتابة أصلاً. ترحيل تدريجي بلا نافذة تعطّل ولا خطوة يدوية.
 */
export async function syncRoomPublic(roomId: string, room: Omit<Room, "id">): Promise<void> {
  await set(ref(rtdb, `roomsPublic/${roomId}`), publicMirror(room));
}

/**
 * قراءة الغرفة **بمحتواها** — لا تنجح إلّا لمن يستحقّ (مجانية، أو
 * مالك، أو مشرف، أو دافع، أو admin). من يريد الاسم والسعر وحدهما —
 * قبل أن يدفع مثلاً — فليقرأ `getRoomPublic`.
 */
export async function getRoom(roomId: string): Promise<Room | null> {
  const snap = await get(ref(rtdb, `rooms/${roomId}`));
  if (!snap.exists()) return null;
  return { id: roomId, ...(snap.val() as Omit<Room, "id">) };
}

/**
 * البيانات الوصفية وحدها — بلا محتوى، ومتاحة لكل مسجَّل.
 *
 * هذا ما تقرأه صفحة الغرفة **قبل** أن تعرف إن كان القارئ يستحقّ
 * المحتوى: لتعرض الاسم والسعر وزرّ الدفع لغير المشترك. قراءة
 * `rooms/$roomId` في تلك اللحظة كانت سترفضها القاعدة الجديدة بحقّ.
 */
export async function getRoomPublic(roomId: string): Promise<Room | null> {
  const snap = await get(ref(rtdb, `roomsPublic/${roomId}`));
  if (!snap.exists()) return null;
  return { id: roomId, ...(snap.val() as Omit<Room, "id">) };
}

// الغرف العامة (RTDB مفهرس على type في القواعد)
export async function listPublicRooms(): Promise<Room[]> {
  /* 🐛 كانت `get(ref(rtdb, "rooms"))` — أي الشجرة كاملةً بكل دردشة
     وكل ملفّ في المنصّة، لعرض قائمة أسماء. الآن المرآة العامّة
     وحدها: بيانات وصفية لا محتوى. */
  const snap = await get(ref(rtdb, "roomsPublic"));
  const val = (snap.val() as Record<string, Omit<Room, "id">>) ?? {};
  return Object.entries(val)
    .map(([id, v]) => ({ id, ...v }))
    .filter((r) => r.type !== "private")
    .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export interface LiveRoom extends Room {
  activeCount: number;
}

const ACTIVE_WINDOW_MS = 60 * 1000; // دقيقة واحدة

// عدّ الحاضرين النشطين (آخر نبض خلال 5 دقائق) في خريطة حضور غرفة
function countActive(roomPresence: Record<string, { lastActive?: number }> | null): number {
  if (!roomPresence) return 0;
  const now = Date.now();
  return Object.values(roomPresence).filter(
    (m) => typeof m.lastActive === "number" && now - m.lastActive < ACTIVE_WINDOW_MS
  ).length;
}

// الغرف العامة النشطة فقط: بها شخص واحد على الأقل نشط خلال آخر 5 دقائق
export async function listLiveRooms(): Promise<LiveRoom[]> {
  const [rooms, presenceSnap] = await Promise.all([
    listPublicRooms(),
    get(ref(rtdb, "presence")),
  ]);
  const presence = (presenceSnap.val() as Record<string, Record<string, { lastActive?: number }>>) ?? {};
  return rooms
    .map((r) => ({ ...r, activeCount: countActive(presence[r.id]) }))
    .filter((r) => r.activeCount >= 1)
    .sort((a, b) => b.activeCount - a.activeCount);
}

// الانضمام بكتابة اسم الغرفة (يشمل الخاصة وغرف الأستاذ غير الظاهرة في القائمة)
export async function findRoomByName(name: string): Promise<Room | null> {
  const trimmed = name.trim();
  if (!trimmed) return null;
  /* البحث بالاسم على المرآة العامّة: الباحث ليس عضواً بعد،
     فلا يجوز أن يقرأ `rooms/$roomId` أصلاً. */
  const q = query(ref(rtdb, "roomsPublic"), orderByChild("name"), equalTo(trimmed));
  const snap = await get(q);
  const val = (snap.val() as Record<string, Omit<Room, "id">>) ?? {};
  const matches = Object.entries(val).map(([id, v]) => ({ id, ...v }));
  if (matches.length === 0) return null;
  // عند تطابق عدة غرف بالاسم، نأخذ الأحدث
  return matches.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
}

export async function sendMessage(
  roomId: string,
  msg: { userId: string; userName: string; text: string }
) {
  await push(ref(rtdb, `rooms/${roomId}/messages`), {
    ...msg,
    type: "text",
    createdAt: Date.now(),
  });
}

export async function sendAttachment(
  roomId: string,
  msg: { userId: string; userName: string; kind: "image" | "file"; dataUrl: string; fileName: string }
) {
  // نخزّن المرفق (base64) في عقدة منفصلة، والرسالة تحمل مرجعه فقط (تبقى الدردشة خفيفة)
  const aRef = push(ref(rtdb, `rooms/${roomId}/attachments`));
  await set(aRef, msg.dataUrl);
  await push(ref(rtdb, `rooms/${roomId}/messages`), {
    userId: msg.userId,
    userName: msg.userName,
    type: msg.kind,
    attachmentId: aRef.key,
    fileName: msg.fileName,
    createdAt: Date.now(),
  });
}

export async function getAttachment(roomId: string, attachmentId: string): Promise<string | null> {
  const snap = await get(ref(rtdb, `rooms/${roomId}/attachments/${attachmentId}`));
  return (snap.val() as string) ?? null;
}

// الاستماع اللحظي للرسائل (آخر 200)
export function listenMessages(roomId: string, cb: (messages: ChatMessage[]) => void) {
  const q = query(ref(rtdb, `rooms/${roomId}/messages`), limitToLast(200));
  return onValue(q, (snap) => {
    const val = (snap.val() as Record<string, Omit<ChatMessage, "id">>) ?? {};
    const msgs = Object.entries(val)
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    cb(msgs);
  });
}

/* ───────── ملفات الغرفة ───────── */
export interface RoomFile {
  id: string;
  name: string;
  uploaderName: string;
  uploaderId?: string;
  attachmentId?: string; // قديم: base64
  driveId?: string; // جديد: Google Drive
  kind?: "notes-pdf" | "upload";
  createdAt: number;
}

export async function addRoomFile(
  roomId: string,
  data: { uploaderId: string; uploaderName: string; name: string; driveId?: string; dataUrl?: string; kind?: RoomFile["kind"] }
): Promise<RoomFile> {
  const createdAt = Date.now();
  const meta: Record<string, unknown> = {
    name: data.name,
    uploaderId: data.uploaderId,
    uploaderName: data.uploaderName,
    createdAt,
  };
  if (data.kind) meta.kind = data.kind;
  let attachmentId: string | undefined;
  if (data.driveId) {
    meta.driveId = data.driveId;
  } else if (data.dataUrl) {
    const aRef = push(ref(rtdb, `rooms/${roomId}/attachments`));
    await set(aRef, data.dataUrl);
    attachmentId = aRef.key ?? undefined;
    if (attachmentId) meta.attachmentId = attachmentId;
  }
  const fileRef = push(ref(rtdb, `rooms/${roomId}/files`));
  await set(fileRef, meta);
  if (!fileRef.key) throw new Error("تعذّر إنشاء معرّف الملف.");
  return {
    id: fileRef.key,
    name: data.name,
    uploaderId: data.uploaderId,
    uploaderName: data.uploaderName,
    createdAt,
    ...(data.driveId ? { driveId: data.driveId } : {}),
    ...(data.kind ? { kind: data.kind } : {}),
    ...(attachmentId ? { attachmentId } : {}),
  };
}

export async function deleteRoomFile(roomId: string, file: RoomFile) {
  await remove(ref(rtdb, `rooms/${roomId}/files/${file.id}`));
  if (file.attachmentId) await remove(ref(rtdb, `rooms/${roomId}/attachments/${file.attachmentId}`));
}

export function listenRoomFiles(roomId: string, cb: (files: RoomFile[]) => void) {
  return onValue(ref(rtdb, `rooms/${roomId}/files`), (snap) => {
    const val = (snap.val() as Record<string, Omit<RoomFile, "id">>) ?? {};
    const list = Object.entries(val).map(([id, f]) => ({ id, ...f }));
    list.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    cb(list);
  });
}

/* ══════════════════════════════════════════════════
   إدارة الغرفة: مشرفون — طرد — حظر — حذف رسائل
══════════════════════════════════════════════════ */

/** ترقية عضو إلى مشرف */
export async function promoteToMod(roomId: string, uid: string) {
  await set(ref(rtdb, `roomLive/${roomId}/mods/${uid}`), true);
}

/** إلغاء ترقية مشرف */
export async function demoteMod(roomId: string, uid: string) {
  await remove(ref(rtdb, `roomLive/${roomId}/mods/${uid}`));
}

/** استماع لقائمة المشرفين */
export function listenMods(roomId: string, cb: (mods: Set<string>) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/mods`), (snap) => {
    cb(new Set(Object.keys(snap.val() ?? {})));
  });
}

/** طرد مؤقّت (إشارة → العميل يغادر تلقائياً) */
export async function kickUser(roomId: string, uid: string) {
  await set(ref(rtdb, `roomLive/${roomId}/kicked/${uid}`), Date.now());
  setTimeout(() => remove(ref(rtdb, `presence/${roomId}/${uid}`)), 1200);
}

/** استماع لإشارة الطرد */
export function listenKicked(roomId: string, uid: string, cb: (kicked: boolean) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/kicked/${uid}`), (snap) => cb(snap.exists()));
}

/** حظر دائم */
export async function banUser(roomId: string, uid: string) {
  await set(ref(rtdb, `bannedUsers/${roomId}/${uid}`), true);
  await kickUser(roomId, uid);
}

/** فك الحظر */
export async function unbanUser(roomId: string, uid: string) {
  await remove(ref(rtdb, `bannedUsers/${roomId}/${uid}`));
}

/** استماع لقائمة المحظورين */
export function listenBanned(roomId: string, cb: (banned: Set<string>) => void) {
  return onValue(ref(rtdb, `bannedUsers/${roomId}`), (snap) => {
    cb(new Set(Object.keys(snap.val() ?? {})));
  });
}

/** حذف رسالة من الغرفة (للمالك فقط) */
export async function deleteRoomMessage(roomId: string, messageId: string) {
  await remove(ref(rtdb, `rooms/${roomId}/messages/${messageId}`));
}

/* ══════════════════════════════════════════
   استفتاء سريع (Quick Poll) في الغرفة
══════════════════════════════════════════ */

export interface RoomPoll {
  question: string;
  options: string[];
  votes: Record<string, number>;
  open: boolean;
  createdAt: number;
}

export async function createPoll(roomId: string, question: string, options: string[]) {
  await set(ref(rtdb, `roomLive/${roomId}/poll`), {
    question: question.trim(),
    options: options.map((o) => o.trim()).filter(Boolean),
    votes: {},
    open: true,
    createdAt: Date.now(),
  });
}

export async function closePoll(roomId: string) {
  await set(ref(rtdb, `roomLive/${roomId}/poll/open`), false);
}

export async function castVote(roomId: string, uid: string, optionIdx: number) {
  await set(ref(rtdb, `roomLive/${roomId}/poll/votes/${uid}`), optionIdx);
}

export function listenPoll(roomId: string, cb: (poll: RoomPoll | null) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/poll`), (snap) => {
    cb((snap.val() as RoomPoll | null) ?? null);
  });
}

/* ══════════════════════════════════════════
   جدول الجلسات الدراسية القادمة
══════════════════════════════════════════ */

export interface ScheduledSession {
  id: string;
  name: string;
  subject: string | null;
  roomId: string;
  ownerId: string;
  ownerName: string;
  scheduledAt: number;
  createdAt: number;
  isPaid?: boolean;
  price?: number;
}

const SESSION_HIDE_AFTER_MS = 60 * 60 * 1000; // أخفِ الجلسة بعد مرور ساعة من وقتها

export async function scheduleSession(input: {
  name: string;
  subject?: string;
  ownerId: string;
  ownerName: string;
  scheduledAt: number;
  ownerRole?: "teacher";
  isPaid?: boolean;
  price?: number;
  branches?: Record<string, boolean> | null;
}): Promise<string> {
  // أنشئ غرفة الجلسة فوراً (تصبح جاهزة للانضمام عند حلول الوقت)
  const roomId = await createRoom({
    name: input.name,
    type: "public",
    subject: input.subject,
    ownerId: input.ownerId,
    ownerName: input.ownerName,
    ownerRole: input.ownerRole,
    isPaid: input.isPaid,
    price: input.price,
  });

  const sData: Record<string, unknown> = {
    name: input.name.trim(),
    subject: input.subject ?? null,
    roomId,
    ownerId: input.ownerId,
    ownerName: input.ownerName,
    scheduledAt: input.scheduledAt,
    createdAt: Date.now(),
  };
  if (input.isPaid && input.price && input.price > 0) {
    sData.isPaid = true;
    sData.price = input.price;
  }
  const sRef = push(ref(rtdb, "scheduledSessions"));
  await set(sRef, sData);
  return roomId;
}

export async function listUpcomingSessions(): Promise<ScheduledSession[]> {
  const snap = await get(ref(rtdb, "scheduledSessions"));
  const val = (snap.val() as Record<string, Omit<ScheduledSession, "id">>) ?? {};
  const now = Date.now();
  return Object.entries(val)
    .map(([id, s]) => ({ id, ...s }))
    .filter((s) => s.scheduledAt > now - SESSION_HIDE_AFTER_MS)
    .sort((a, b) => a.scheduledAt - b.scheduledAt);
}

export function listenUpcomingSessions(cb: (sessions: ScheduledSession[]) => void) {
  return onValue(ref(rtdb, "scheduledSessions"), (snap) => {
    const val = (snap.val() as Record<string, Omit<ScheduledSession, "id">>) ?? {};
    const now = Date.now();
    const list = Object.entries(val)
      .map(([id, s]) => ({ id, ...s }))
      .filter((s) => s.scheduledAt > now - SESSION_HIDE_AFTER_MS)
      .sort((a, b) => a.scheduledAt - b.scheduledAt);
    cb(list);
  });
}

export async function deleteScheduledSession(id: string) {
  await remove(ref(rtdb, `scheduledSessions/${id}`));
}

/* ══════════════════════════════════════════
   مؤقّت الغرفة (Lesson Timer)
══════════════════════════════════════════ */
export interface RoomTimer {
  duration: number; startedAt: number; active: boolean; label?: string;
}
export async function setRoomTimer(roomId: string, timer: RoomTimer | null) {
  if (timer === null) {
    await set(ref(rtdb, `roomLive/${roomId}/timer`), null);
    return;
  }
  // Firebase يرفض الكائن إن احتوى undefined — نبني كائناً نظيفاً
  const clean: RoomTimer = {
    duration: timer.duration,
    startedAt: timer.startedAt,
    active: timer.active,
  };
  if (timer.label) clean.label = timer.label;
  await set(ref(rtdb, `roomLive/${roomId}/timer`), clean);
}
export function listenRoomTimer(roomId: string, cb: (t: RoomTimer | null) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/timer`), (snap) => {
    cb((snap.val() as RoomTimer | null) ?? null);
  });
}
/* ══════════════════════════════════════════
   الملاحظات المشتركة (Shared Notes)
══════════════════════════════════════════ */
/* ── نسختان: مسودّة ومنشور ──
   كان ما يكتبه الأستاذ يصل الطلبة **حرفاً بحرف** لحظة كتابته: يرون
   الأخطاء المطبعية، والجملة نصفها، والفكرة تُكتب ثمّ تُحذف. الآن:

     `notesDraft/$roomId`     — مسودّة الأستاذ والمشرفين، لا يقرأها التلميذ.
     `roomLive/$roomId/notes` — النسخة المنشورة، وهي التي يقرأها التلميذ.

   المسودّة عقدة **مستقلّة** لا تحت `roomLive`: ذلك المسار يمنح القراءة
   لكل مصادَق عليه، وقاعدة `.read` على عقدة ابنة لا تستطيع سحب صلاحية
   منحها الأب — فلو وضعناها تحته لكانت مقروءة للطلبة فعلياً.

   والتلميذ لا يزال يقرأ نفس المسار القديم، فلا شيء يتعطّل، وكل ملاحظة
   محفوظة سابقاً تبقى منشورة كما هي. */
export async function saveRoomNotes(roomId: string, text: string) {
  await set(ref(rtdb, `roomLive/${roomId}/notes`), text);
}
export function listenRoomNotes(roomId: string, cb: (text: string) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/notes`), (snap) => {
    cb((snap.val() as string) ?? "");
  });
}
export async function saveRoomNotesDraft(roomId: string, text: string) {
  await set(ref(rtdb, `notesDraft/${roomId}`), text);
}
/** `null` تعني «لا مسودّة بعد» — فنبدأ من النسخة المنشورة */
export function listenRoomNotesDraft(roomId: string, cb: (text: string | null) => void) {
  return onValue(ref(rtdb, `notesDraft/${roomId}`), (snap) => {
    cb(snap.exists() ? ((snap.val() as string) ?? "") : null);
  });
}

/* ══════════════════════════════════════════
   مؤشّر "يكتب الآن..." في الدردشة
══════════════════════════════════════════ */
export async function setTyping(roomId: string, uid: string, name: string, typing: boolean) {
  const r = ref(rtdb, `roomLive/${roomId}/typing/${uid}`);
  if (typing) {
    await set(r, { name, at: Date.now() });
    onDisconnect(r).remove();
  } else {
    await remove(r);
  }
}

export function listenTyping(roomId: string, cb: (typers: { uid: string; name: string }[]) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/typing`), (snap) => {
    const val = (snap.val() as Record<string, { name: string; at: number }>) ?? {};
    const now = Date.now();
    // نتجاهل المؤشرات الأقدم من 6 ثوانٍ (في حال لم تُحذف)
    const list = Object.entries(val)
      .filter(([, v]) => now - v.at < 6000)
      .map(([uid, v]) => ({ uid, name: v.name }));
    cb(list);
  });
}

/* ══════════════════════════════════════════
   رفع اليد (مع طابور حسب الأسبقية)
══════════════════════════════════════════ */
export interface RaisedHand { uid: string; name: string; at: number }

export async function raiseHand(roomId: string, uid: string, name: string) {
  const r = ref(rtdb, `roomLive/${roomId}/hands/${uid}`);
  await set(r, { name, at: Date.now() });
  onDisconnect(r).remove();
}

export async function lowerHand(roomId: string, uid: string) {
  await remove(ref(rtdb, `roomLive/${roomId}/hands/${uid}`));
}

export function listenHands(roomId: string, cb: (hands: RaisedHand[]) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/hands`), (snap) => {
    const val = (snap.val() as Record<string, { name: string; at: number }>) ?? {};
    const list = Object.entries(val)
      .map(([uid, v]) => ({ uid, name: v.name, at: v.at }))
      .sort((a, b) => a.at - b.at); // الأقدم أولاً (طابور)
    cb(list);
  });
}

/* ══════════════════════════════════════════
   الملف المعروض حالياً (يتحكّم به المالك ويظهر للجميع)
══════════════════════════════════════════ */
export async function setActiveFile(roomId: string, fileId: string | null) {
  const r = ref(rtdb, `roomLive/${roomId}/activeFile`);
  if (fileId) await set(r, fileId);
  else await remove(r);
}

export function listenActiveFile(roomId: string, cb: (fileId: string | null) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/activeFile`), (snap) => {
    cb((snap.val() as string) ?? null);
  });
}

/* ══════════════════════════════════════════
   حالة المضيف + رسالة الترحيب (تظهر للتلاميذ)
══════════════════════════════════════════ */
export type OwnerStatus = "available" | "busy" | "brb"; // متفرّغ / مشغول / سأعود

export async function setOwnerStatus(roomId: string, status: OwnerStatus) {
  await set(ref(rtdb, `roomLive/${roomId}/ownerStatus`), status);
}

export function listenOwnerStatus(roomId: string, cb: (status: OwnerStatus) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/ownerStatus`), (snap) => {
    cb((snap.val() as OwnerStatus) ?? "available");
  });
}

export async function setWelcomeMessage(roomId: string, msg: string) {
  await set(ref(rtdb, `roomLive/${roomId}/welcomeMsg`), msg || null);
}

export function listenWelcomeMessage(roomId: string, cb: (msg: string) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/welcomeMsg`), (snap) => {
    cb((snap.val() as string) ?? "");
  });
}

/* ══════════════════════════════════════════
   الأسئلة المجهولة (Anonymous Questions)
   تصل للأستاذ دون إظهار اسم التلميذ
   roomLive/{roomId}/anonQuestions/{qid} = { text, at, answered? }
══════════════════════════════════════════ */
export interface AnonQuestion {
  id: string;
  text: string;
  at: number;
  answered?: boolean;
}

export async function sendAnonQuestion(roomId: string, text: string) {
  const clean = text.trim();
  if (!clean) return;
  await push(ref(rtdb, `roomLive/${roomId}/anonQuestions`), {
    text: clean.slice(0, 500),
    at: Date.now(),
  });
}

export function listenAnonQuestions(roomId: string, cb: (qs: AnonQuestion[]) => void) {
  return onValue(ref(rtdb, `roomLive/${roomId}/anonQuestions`), (snap) => {
    const val = (snap.val() as Record<string, Omit<AnonQuestion, "id">>) ?? {};
    const list = Object.entries(val)
      .map(([id, q]) => ({ id, ...q }))
      .sort((a, b) => b.at - a.at);
    cb(list);
  });
}

export async function markAnonAnswered(roomId: string, qid: string) {
  await update(ref(rtdb, `roomLive/${roomId}/anonQuestions/${qid}`), { answered: true });
}

export async function deleteAnonQuestion(roomId: string, qid: string) {
  await remove(ref(rtdb, `roomLive/${roomId}/anonQuestions/${qid}`));
}
